import { useEffect, useState, type ChangeEvent } from 'react'
import { Box, IconButton, Input, Tooltip, Typography } from '@mui/material'
import NotesIcon from '@mui/icons-material/Notes'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CfpSelect from './CfpSelect'
import { useDebouncedCallback } from '../common/useDebouncedCallback'
import { BORDER_BLACK, COLOR_PRIMARY, FS_SM, FS_XS, ICON_SM } from '../common/theme'
import { ReplyLimit } from '../types'
import type { CfpSelectOption } from '../types'

// Preset answer lengths. Word vs char count is picked off the current mode
// when the preset is applied - the char column is roughly 5× the word
// column so the UI reads the same in either mode.
export const WORD_LIMIT_PRESETS: { label: string; words: number; chars: number }[] = [
  { label: 'Brief', words: 50, chars: 250 },
  { label: 'Short', words: 150, chars: 1250 },
  { label: 'Medium', words: 500, chars: 2500 },
  { label: 'Long', words: 1000, chars: 5000 },
]
const PRESETS = WORD_LIMIT_PRESETS

interface TaWordLimitProps {
  replylimit: ReplyLimit | undefined
  onChange: (limit: any) => void
  title?: string
}

// Compact "N words" / "N chars" pill. The icon on the left toggles between
// word and char modes, the preset picker (Brief/Short/Medium/Long) is a
// borderless CfpSelect, the centre is a borderless number input, the
// trailing label shows the unit. The whole chip picks up the gold focus
// ring used by the rest of the app's frames.
export default function TaWordLimit({ replylimit, onChange, title }: TaWordLimitProps) {
  const isCharCount = !!replylimit?.charcount
  const propValue = replylimit?.answerlimit ?? 150

  // Local buffer for the number - the parent's `onChange` ultimately fans
  // out through AppContext.updateQuestion + setCurrentProject /
  // setCurrentQuestion which forces a project-wide re-render, so emitting
  // on every click felt laggy. We update local immediately, schedule a
  // debounced emit, and re-sync from the prop when it changes externally.
  const [value, setValue] = useState(propValue)
  useEffect(() => { setValue(propValue) }, [propValue])

  const debounced = useDebouncedCallback(() => {
    onChange({ ...replylimit, answerlimit: value })
  }, 250)

  // The preset label is derived, not stored - if the current `answerlimit`
  // matches one of the preset columns (words or chars depending on mode)
  // we display that preset's name, otherwise the chip reads "Custom".
  const selectedLabel = PRESETS.find(p => (isCharCount ? p.chars : p.words) === value)?.label

  const presetOptions: CfpSelectOption<string>[] = PRESETS.map(p => ({
    value: p.label,
    label: p.label,
    // Greyed-out trailing hint shows the count this preset maps to in the
    // current unit (words vs chars).
    hint: `${isCharCount ? p.chars : p.words} ${isCharCount ? 'chars' : 'words'}`,
  }))

  const handlePresetChange = (label: string) => {
    const preset = PRESETS.find(p => p.label === label)
    if (!preset) return
    const next = isCharCount ? preset.chars : preset.words
    debounced.cancel()
    setValue(next)
    onChange({ ...replylimit, answerlimit: next })
  }

  // Typing: update the local buffer right away and schedule a debounced
  // emit. Blur flushes so the parent always catches the final value.
  const handleLimitChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(Number(e.target.value))
    debounced.schedule()
  }
  const handleLimitBlur = () => debounced.flush()

  // Step matches the legacy number-input step: coarse 100 for chars, 10 for
  // words. Custom up/down buttons clamp to the same min/max as the input
  // and route through the same debounced emit so rapid clicks feel snappy.
  const stepBy = (delta: number) => {
    const next = Math.max(0, Math.min(9000, value + delta))
    if (next === value) return
    setValue(next)
    debounced.schedule()
  }
  const step = isCharCount ? 100 : 10

  // Toggling word/char mode keeps the preset (Brief/Short/Medium/Long)
  // sticky - if the current count matches a preset in the *old* mode, we
  // snap to that preset's value in the *new* mode so "Short" stays
  // "Short" across a word ↔ char swap. A custom value keeps its number.
  const toggleMode = () => {
    const nextCharCount = !isCharCount
    const matched = PRESETS.find(p => (isCharCount ? p.chars : p.words) === value)
    const nextValue = matched
      ? (nextCharCount ? matched.chars : matched.words)
      : value
    debounced.cancel()
    setValue(nextValue)
    onChange({ ...replylimit, charcount: nextCharCount, answerlimit: nextValue })
  }

  const unitLabel = isCharCount ? (value === 1 ? 'char' : 'chars') : (value === 1 ? 'word' : 'words')

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {title && (
        <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem' }}>{title}</Typography>
      )}

      {/* Whole-pill tooltip - explains the field. Inner tooltips on the
          mode toggle and preset chip still work because MUI nests them. */}
      <Tooltip title="Answer size the AI will target" placement="top" enterDelay={400}>
        <Box
          data-testid="taword_limit"
          sx={{
            display: 'inline-flex', alignItems: 'center',
            pl: 0.25, pr: 0.75,
            border: 1, borderColor: 'divider', borderRadius: 999,
            bgcolor: 'background.paper',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
            '&:hover': { borderColor: COLOR_PRIMARY },
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: (t: any) => `0 0 0 3px ${t.palette.primary.main}1f`,
            },
          }}
        >
          {/* Mode toggle - clicking the icon flips word ↔ char count. */}
          <Tooltip title={isCharCount ? 'Counting characters · click for words' : 'Counting words · click for characters'}>
            <Box
              component="button"
              type="button"
              data-testid="taword_modetoggle"
              onClick={toggleMode}
              sx={{
                border: 0, p: 0, m: 0, pl: 0.5,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 999,
                cursor: 'pointer', background: 'transparent',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              {isCharCount
                ? <TextFieldsIcon sx={{ fontSize: ICON_SM }} />
                : <NotesIcon sx={{ fontSize: ICON_SM }} />}
            </Box>
          </Tooltip>

          {/* Preset picker - borderless CfpSelect sitting inside the pill.
              `value` is derived from the current count; when no preset
              matches, the placeholder reads "Custom". */}
          <CfpSelect<string>
            value={selectedLabel ?? ''}
            options={presetOptions}
            onChange={handlePresetChange}
            placeholder="Custom"
            borderless
            testId="taword_preset_select"
          />

          {/* Number sub-group - the steppers only reveal when the cursor is
              over this group (the input, the steppers themselves, or the
              unit label) or while the input has focus. Hovering elsewhere
              on the pill (mode icon / preset chip) leaves them hidden. */}
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center',
              position: 'relative',
              // '&:hover .taword-steppers, &:focus-within .taword-steppers': {
              //   display: 'inline-flex',
              //   position: 'absolute',
              //   right: 14
              // },
              '&:hover .taword-steppers': {
                display: 'inline-flex',
                position: 'absolute',
                right: 14
              },
            }}
          >
            <Input
              type="number"
              disableUnderline
              value={value}
              inputProps={{ min: 0, max: 9000, step, 'aria-label': unitLabel }}
              onChange={handleLimitChange}
              onBlur={handleLimitBlur}
              sx={{
                // Width sized for ~4 digits. Native spinners are hidden in
                // favour of the custom up/down chevron column to the right,
                // which gives us a consistent (and bigger) hit area across
                // Chrome / Firefox / Safari.
                width: 34,
                mr: 0.5,
                flex: 'none',
                '& input': {
                  mb: -0.1,
                  fontSize: FS_SM, fontWeight: 500,
                  py: 0, px: 0, textAlign: 'right',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none', margin: 0,
                  },
                  MozAppearance: 'textfield',
                },
              }}
            />

            <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.55, ml: 0.1, mb: -0.1 }}>
              {unitLabel}
            </Typography>

            <Box
              className="taword-steppers"
              sx={{ display: 'none', flexDirection: 'column', ml: 0.25, background: 'white' }}
            >
              <IconButton
                size="small"
                data-testid="taword_step_up"
                onClick={() => stepBy(step)}
                disabled={value >= 9000}
                sx={{ p: 0, width: 18, height: 12, borderRadius: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
              <IconButton
                size="small"
                data-testid="taword_step_down"
                onClick={() => stepBy(-step)}
                disabled={value <= 0}
                sx={{ p: 0, width: 18, height: 12, borderRadius: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: ICON_SM }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  )
}
